import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = '33612975564';
const WHATSAPP_HEADER = 'Renseignements location villa Nomade Ile de Ré';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_HEADER)}`;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'm0',
      role: 'assistant',
      content:
        "Bonjour et bienvenue à la Villa Nomade ! 👋 Je réponds à toutes vos questions (dans votre langue) sur la villa, l'Île de Ré, les tarifs, la réservation… Comment puis-je vous aider ?"
    }
  ]);
  const scrollRef = useRef(null);
  const msgId = useRef(0);
  const mid = () => `m${++msgId.current}`;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { id: mid(), role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId
      });
      if (res.data?.session_id) setSessionId(res.data.session_id);
      setMessages((prev) => [
        ...prev,
        { id: mid(), role: 'assistant', content: res.data.reply, images: res.data.images || [] }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: mid(),
          role: 'assistant',
          content:
            "Désolé, je rencontre un souci technique. Vous pouvez nous contacter directement via WhatsApp ou le formulaire de contact."
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const saveEmail = async (e) => {
    e?.preventDefault();
    const email = emailInput.trim();
    if (!email) return;
    try {
      await axios.post(`${API}/chat/lead`, { email, session_id: sessionId });
      setEmailSaved(true);
      setMessages((prev) => [
        ...prev,
        { id: mid(), role: 'assistant', content: 'Merci ! Le propriétaire pourra vous recontacter à cette adresse.' }
      ]);
    } catch (err) {
      setEmailSaved(true);
    }
  };

  return (
    <>
      {/* Floating buttons */}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
        {/* WhatsApp */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contacter sur WhatsApp"
          className="group flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full shadow-lg pl-4 pr-5 py-3 transition-all duration-300 hover:scale-105"
        >
          <WhatsAppIcon className="w-6 h-6" />
          <span className="hidden sm:inline text-sm font-semibold">WhatsApp</span>
        </a>

        {/* Chat toggle */}
        {!open && (
          <button
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le chat"
            className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white rounded-full shadow-lg pl-4 pr-5 py-3 transition-all duration-300 hover:scale-105"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="hidden sm:inline text-sm font-semibold">Une question ?</span>
          </button>
        )}
      </div>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[70] w-[calc(100vw-3rem)] max-w-sm h-[70vh] max-h-[560px] flex flex-col bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-700 to-amber-800 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold leading-tight">Assistant Villa Nomade</div>
                <div className="text-xs text-amber-100">Réponses dans toutes les langues</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fermer le chat" className="text-white/90 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${m.role === 'user' ? '' : 'w-full'}`}>
                  {m.content && (
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-amber-700 text-white rounded-br-sm ml-auto w-fit'
                          : 'bg-white text-stone-800 border border-stone-200 rounded-bl-sm w-fit'
                      }`}
                    >
                      {m.content}
                    </div>
                  )}
                  {m.images && m.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {m.images.map((img, j) => (
                        <a
                          key={j}
                          href={img.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative block rounded-lg overflow-hidden border border-stone-200 shadow-sm"
                          title={img.caption}
                        >
                          <img
                            src={img.url}
                            alt={img.caption}
                            loading="lazy"
                            className="w-full h-24 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] px-1.5 py-1 truncate">
                            {img.caption}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-stone-500 border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  L'assistant écrit…
                </div>
              </div>
            )}
          </div>

          {/* Email capture (optionnel) */}
          {!emailSaved && (
            <form onSubmit={saveEmail} className="px-3 py-2 border-t border-stone-100 bg-amber-50/60 flex items-center gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Votre email (facultatif) pour être recontacté"
                className="flex-1 border border-amber-200 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button type="submit" className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-full px-3 py-1.5 transition-colors">
                OK
              </button>
            </form>
          )}

          {/* WhatsApp shortcut */}
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-[#25D366]/10 text-[#128C4B] hover:bg-[#25D366]/20 text-xs font-medium py-2 transition-colors border-t border-stone-100"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Préférez discuter sur WhatsApp ? Cliquez ici
          </a>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t border-stone-200 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écrivez votre message…"
              className="flex-1 border border-stone-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Envoyer"
              className="w-10 h-10 flex items-center justify-center bg-amber-700 hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
